import { connect } from 'react-redux';
import { Button } from '@iwoca/orion';
import { differenceInDays } from 'date-fns';

import { getPrimaryAccountLoanStatus } from '../../../../multiLoanDashboard/multiLoanDashboardSelector';
import * as onlineAccountSelectors from '../../../../../api/lendingAPI/OnlineAccountsSelectors';
import { OPEN_MODAL } from '../../../../../iwModals/Modals.actions';
import { getLogoFromProvider } from '../../../../tradingData/linkedAccounts/LinkedAccountsHelpers';

import DashboardCTA from '../../../../multiLoanDashboard/dashboardComponents/DashboardCTA/DashboardCTA';

import DashboardCTAStyles from '../../../../multiLoanDashboard/dashboardComponents/DashboardCTA/DashboardCTA.module.css';

const mapStateToProps = (state) => {
    return {
        provider: onlineAccountSelectors.getLatestOnlineAccountProvider(state),
        onlineAccountsLoaded: state.revenueBasedLoanDashboard.outstandingReady,
        isOpenBankingNotActive:
            onlineAccountSelectors.isLatestOnlineAccountNotActive(state),
        loanStatus: getPrimaryAccountLoanStatus(state),
        openBankingExpiryDate:
            onlineAccountSelectors.getLatestOnlineAccountExpiryDate(state),
        openBankingStatus:
            onlineAccountSelectors.getLatestOnlineAccountStatus(state)
    };
};

const mapDispatchToProps = (dispatch) => ({
    openLinkBankingModal: () =>
        dispatch({
            type: OPEN_MODAL,
            id: 'relinkBankingModal'
        })
});

export function calculateRelinkDateFromExpiry(expiryDate) {
    const relinkDate = new Date(expiryDate);
    const currentDate = new Date();

    return differenceInDays(relinkDate, currentDate);
}

const OpenBankingLink = ({
    provider,
    openLinkBankingModal,
    onlineAccountsLoaded,
    isOpenBankingNotActive,
    isOpenBankingExpiringSoon,
    openBankingExpiryDate,
    openBankingStatus,
    className
}) => {
    if (!onlineAccountsLoaded || !provider) {
        return null;
    }

    const daysUntilRelink = calculateRelinkDateFromExpiry(
        openBankingExpiryDate
    );
    const ProviderLogo = getLogoFromProvider(provider);

    if (
        openBankingStatus === onlineAccountSelectors.OPEN_BANKING_STATUS.EXPIRED
    ) {
        return (
            <DashboardCTA
                icon={<ProviderLogo />}
                className={className}
                data-testid="rbl-open-banking-expired-action"
                title="Banking link expired"
                description="Your link has expired please relink your account"
                cta={
                    <Button
                        variant="action"
                        className={DashboardCTAStyles.link}
                        onClick={openLinkBankingModal}
                    >
                        Update banking link
                    </Button>
                }
            />
        );
    }

    if (
        isOpenBankingNotActive &&
        openBankingStatus !== onlineAccountSelectors.OPEN_BANKING_STATUS.EXPIRED
    ) {
        return (
            <DashboardCTA
                icon={<ProviderLogo />}
                className={className}
                data-testid="rbl-open-banking-unlinked-action"
                title="Banking link missing"
                description="We couldn't find a bank link - please relink your account"
                cta={
                    <Button
                        variant="action"
                        className={DashboardCTAStyles.link}
                        onClick={openLinkBankingModal}
                    >
                        Update banking link
                    </Button>
                }
            />
        );
    }

    if (isOpenBankingExpiringSoon) {
        return (
            <DashboardCTA
                icon={<ProviderLogo />}
                className={className}
                data-testid="rbl-open-banking-expiring-soon-action"
                title="Refresh your banking link now"
                description={
                    <span>
                        It expires in{' '}
                        <strong>
                            {`${daysUntilRelink} day${
                                daysUntilRelink > 1 ? 's' : ''
                            }`}
                        </strong>{' '}
                        - please refresh the link to bring it back to 90 days
                    </span>
                }
                cta={
                    <Button
                        variant="action"
                        className={DashboardCTAStyles.link}
                        onClick={openLinkBankingModal}
                    >
                        Update banking link
                    </Button>
                }
            />
        );
    }

    return (
        <DashboardCTA
            icon={<ProviderLogo />}
            className={className}
            data-testid="rbl-open-banking-action"
            title="Bank linked"
            description={
                <span>
                    You have <strong>{daysUntilRelink} days</strong> left until
                    the link expires. You can relink to bring it back to 90 days
                    at any time.
                </span>
            }
            cta={
                <Button
                    variant="tertiary"
                    className={DashboardCTAStyles.link}
                    onClick={openLinkBankingModal}
                >
                    Relink bank
                </Button>
            }
        />
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(OpenBankingLink);
